import React, { useEffect, useState } from "react";
import Button, { ButtonType } from "../../components/Assets/Button";
import ServiceTabs from "../../components/ServiceManagement/ServiceTabs";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  addServicePartner,
  addServiceTicket,
  getServiceErrorCodes,
  getServicePartners,
  updateServicePartner,
} from "../../store/slices/serviceManagementSlice";
import toast from "react-hot-toast";
import AddServicePartnerModal from "../../components/ServiceManagement/AddServicePartnerModal";
import ServicePartnerTable from "../../components/ServiceManagement/ServicePartnerTable";
import AddServiceTicketModal from "../../components/ServiceManagement/AddServiceTicketModal";
import ServiceKanbanBoard from "../../components/ServiceManagement/ServiceKanbanBoard";
import { getUserId } from "../../utils/auth";
import {
  IAddress,
  ServiceTicketType,
  IServicePartner,
} from "../../models/serviceManagement.model";
import SearchInput from "../../components/Search/SearchInput";
import { getTalukasByDistrict } from "../../store/slices/kanbanSlice";
import ViewServicePartnerModal from "../../components/ServiceManagement/UpdateServicePartnerModal";

const ServiceManagement: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const [activeTab, setActiveTab] = useState<"services" | "partners">(
    "services",
  );
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [isServiceTicketModalOpen, setIsServiceTicketModalOpen] =
    useState(false);

  // Service Partner Fields
  const [name, setName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [address, setAddress] = useState({
    addressLine1: "",
    state: "",
    district: "",
    taluka: "",
    pincode: "",
  });
  const [companyName, setCompanyName] = useState("");

  // Service Ticket Fields
  const [ticketType, setTicketType] = useState<ServiceTicketType>(
    ServiceTicketType.inverterFault,
  );
  const [epcName, setEpcName] = useState("");
  const [epcMobileNumber, setEpcMobileNumber] = useState("");
  const [epcPincode, setEpcPincode] = useState("");
  const [siteAddress, setSiteAddress] = useState("");

  const [customerName, setCustomerName] = useState("");
  const [customerMobileNumber, setCustomerMobileNumber] = useState("");
  const [customerAddress, setCustomerAddress] = useState<IAddress>({
    addressLine1: "",
    state: "",
    district: "",
    pincode: "",
  });
  const [serialNumber, setSerialNumber] = useState("");
  const [loggerSerialNo, setLoggerSerialNo] = useState<string | undefined>(
    undefined,
  );
  const [complainNote, setComplainNote] = useState<string | undefined>(
    undefined,
  );

  const [selectedPhase, setSelectedPhase] = useState<"single" | "three" | "">(
    "",
  );

  const [inverterModel, setInverterModel] = useState<string | undefined>(
    undefined,
  );
  const [selectedErrorCode, setSelectedErrorCode] = useState<
    Record<string, string> | undefined
  >(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [district, setDistrict] = useState("");
  const [taluka, setTaluka] = useState("");
  const [pincode, setPincode] = useState("");

  const [selectedPartner, setSelectedPartner] =
    useState<IServicePartner | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const addServicePartnerloading = useSelector(
    (state: RootState) =>
      state.serviceManagement.loadingState.addServicePartnerLoading,
  );
  const fetchServicePartnersLoading = useSelector(
    (state: RootState) =>
      state.serviceManagement.loadingState.fetchServicePartnersLoading,
  );
  const addServiceTicketLoading = useSelector(
    (state: RootState) =>
      state.serviceManagement.loadingState.addServiceTicketLoading,
  );
  const servicePartners = useSelector(
    (state: RootState) => state.serviceManagement.servivePartners,
  );
  const updatingServicePartnerLoading = useSelector(
    (state: RootState) =>
      state.serviceManagement.loadingState.updateServicePartnerLoading,
  );
  const userId = getUserId();
  const statesData = useSelector((state: RootState) => state.kanban.statesData);
  const talukasData = useSelector(
    (state: RootState) => state.kanban.talukasData,
  );

  const districts = React.useMemo(() => {
    if (!statesData || statesData.length === 0) return [];

    return Array.from(
      new Set(statesData.flatMap((s) => s.districts || [])),
    ).sort();
  }, [statesData]);

  const availableTalukas = React.useMemo(() => {
    return talukasData?.taluka || [];
  }, [talukasData]);

  const fetchPartners = (
    params?: Partial<{
      searchQuery: string;
      district: string;
      taluka: string;
      pincode: string;
    }>,
  ) => {
    dispatch(
      getServicePartners({
        searchQuery,
        district,
        pincode,
        ...params,
      }),
    );
  };

  const handleUpdatePartner = async (
    id: string,
    payload: Partial<IServicePartner>,
  ) => {
    try {
      await dispatch(updateServicePartner({ id, payload })).unwrap();
      setIsViewModalOpen(false);
    } catch (err: any) {
      throw err;
    }
  };

  const handleServicePartnerSearch = (term: string) => {
    setSearchQuery(term);
    fetchPartners({ searchQuery: term });
  };

  const handleDistrictChange = (val: string) => {
    setDistrict(val);
    setTaluka("");
    if (val) {
      dispatch(getTalukasByDistrict(val));
    }
    fetchPartners({ district: val });
  };

  const handleTalukaChange = (val: string) => {
    setTaluka(val);
    fetchPartners({ taluka: val });
  };

  const handlePincodeChange = (val: string) => {
    setPincode(val);
    fetchPartners({ pincode: val });
  };

  useEffect(() => {
    dispatch(getServicePartners(undefined));
    dispatch(getServiceErrorCodes());
  }, [dispatch]);

  const resetServiceTicketFields = () => {
    setTicketType(ServiceTicketType.inverterFault);
    setEpcName("");
    setEpcMobileNumber("");
    setEpcPincode("");
    setSiteAddress("");
    setCustomerName("");
    setCustomerMobileNumber("");
    setCustomerAddress({
      addressLine1: "",
      state: "",
      district: "",
      pincode: "",
    });
    setSerialNumber("");
    setLoggerSerialNo(undefined);
    setComplainNote(undefined);
    setSelectedPhase("");
    setInverterModel(undefined);
    setSelectedErrorCode(undefined);
  };

  const resetServicePartnerFields = () => {
    setName("");
    setMobileNumber("");
    setAddress({
      addressLine1: "",
      state: "",
      district: "",
      taluka: "",
      pincode: "",
    });
  };

  const handleAddServicePartner = async () => {
    if (!name) {
      toast.error("Name is required");
      return;
    }
    if (!mobileNumber) {
      toast.error("Mobile number is required");
      return;
    }
    if (!address.district) {
      toast.error("District is required");
      return;
    }

    if (!/^\d{10}$/.test(mobileNumber)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!/^\d{6}$/.test(address.pincode)) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }

    const payload = {
      name,
      mobileNumber,
      address,
      companyName,
    };

    try {
      await dispatch(addServicePartner(payload)).unwrap();

      setName("");
      setMobileNumber("");
      setAddress({
        addressLine1: "",
        state: "",
        district: "",
        taluka: "",
        pincode: "",
      });

      setIsPartnerModalOpen(false);
    } catch (err) {
      console.error("Error adding service partner:", err);
    }
  };

  const handleAddServiceTicket = async () => {
    if (!customerName || !customerMobileNumber) {
      toast.error("Customer name and mobile number are required");
      return;
    }

    if (!/^\d{10}$/.test(customerMobileNumber)) {
      toast.error("Please enter a valid 10-digit customer mobile number");
      return;
    }

    if (ticketType === ServiceTicketType.inverterFault) {
      if (
        !customerAddress.addressLine1 ||
        !customerAddress.state ||
        !customerAddress.district ||
        !customerAddress.pincode
      ) {
        toast.error("Complete customer address is required for inverter fault");
        return;
      }

      if (!/^\d{6}$/.test(customerAddress.pincode)) {
        toast.error("Please enter a valid 6-digit customer pincode");
        return;
      }

      if (epcMobileNumber && !/^\d{10}$/.test(epcMobileNumber)) {
        toast.error("Please enter a valid 10-digit EPC mobile number");
        return;
      }

      if (epcPincode && !/^\d{6}$/.test(epcPincode)) {
        toast.error("Please enter a valid 6-digit EPC pincode");
        return;
      }
    }

    if (!serialNumber) {
      toast.error("Serial number is required");
      return;
    }

    if (ticketType === ServiceTicketType.monitoring) {
      if (!loggerSerialNo) {
        toast.error("Logger serial number is required for type monitoring");
        return;
      }
      if (!inverterModel) {
        toast.error("Inverter model is required for type monitoring");
        return;
      }
    }

    const payload = {
      ticketType,
      epcName,
      epcMobileNumber,
      epcPincode,
      siteAddress,
      customerName,
      customerMobileNumber,
      customerAddress,
      loggerSerialNo,
      serialNumber,
      complainNote,
      inverterModel,
      errorCode:
        selectedErrorCode?.code && selectedErrorCode?.message
          ? `${selectedErrorCode.code} - ${selectedErrorCode.message}`
          : undefined,
      userId,
    };

    try {
      await dispatch(addServiceTicket(payload)).unwrap();

      resetServiceTicketFields();

      setIsServiceTicketModalOpen(false);
    } catch (err) {
      console.error("Error adding service partner:", err);
    }
  };

  return (
    <div>
      {/* Tabs */}
      <ServiceTabs activeTab={activeTab} onChange={setActiveTab} />

      <div className="px-4">
        <div className="flex items-center justify-between p-2 rounded-md relative">
          <div className="flex items-center gap-3 w-full">
            {activeTab === "partners" && (
              <div className="flex items-center gap-3 flex-wrap">
                {/* Search (flex-grow) */}
                <div>
                  <SearchInput
                    placeholder="Search by name or mobile number"
                    className="w-96 border px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    onSearch={handleServicePartnerSearch}
                  />
                </div>

                {/* District */}
                <div className="flex flex-col min-w-[180px]">
                  <select
                    value={district}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    className="p-2 text-xs rounded-md border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Filter by district</option>

                    {districts.map((d: string) => (
                      <option key={d} value={d.toLowerCase()}>
                        {d.capitalizeWords()}
                      </option>
                    ))}
                  </select>
                </div>

                {district && (
                  <div className="flex flex-col min-w-[160px]">
                    <select
                      value={taluka}
                      onChange={(e) => handleTalukaChange(e.target.value)}
                      className="p-2 text-xs rounded-md border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">Filter by taluka</option>
                      {availableTalukas.map((t: string) => (
                        <option key={t} value={t.toLowerCase()}>
                          {t.capitalizeWords()}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Pincode */}
                <input
                  type="text"
                  placeholder="Filter by pincode"
                  value={pincode}
                  maxLength={6}
                  onChange={(e) => handlePincodeChange(e.target.value)}
                  className="border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm w-36"
                />

                {/* Clear */}
                {(searchQuery || district || pincode) && (
                  <Button
                    type={ButtonType.Cancel}
                    onClick={() => {
                      setSearchQuery("");
                      setDistrict("");
                      setPincode("");
                      dispatch(getServicePartners(undefined));
                    }}
                    className="text-xs"
                  >
                    Clear
                  </Button>
                )}

                {/* Add Button */}
                <Button
                  type={ButtonType.Submit}
                  className="text-xs whitespace-nowrap"
                  onClick={() => setIsPartnerModalOpen(true)}
                >
                  Add Service Partner
                </Button>
              </div>
            )}

            {activeTab === "services" && (
              <Button
                type={ButtonType.Submit}
                className="text-xs"
                onClick={() => setIsServiceTicketModalOpen(true)}
              >
                Create Ticket
              </Button>
            )}
          </div>
        </div>
      </div>
      <AddServicePartnerModal
        isOpen={isPartnerModalOpen}
        onClose={() => {
          setIsPartnerModalOpen(false);
          resetServicePartnerFields();
        }}
        name={name}
        mobileNumber={mobileNumber}
        address={address}
        companyName={companyName}
        setName={setName}
        setMobileNumber={setMobileNumber}
        setAddress={setAddress}
        setCompanyName={setCompanyName}
        loading={addServicePartnerloading}
        handleAddServicePartner={handleAddServicePartner}
      />
      {activeTab === "partners" && (
        <>
          <ServicePartnerTable
            servicePartners={servicePartners}
            loading={fetchServicePartnersLoading}
            onRowClick={(partner) => {
              setSelectedPartner(partner);
              setIsViewModalOpen(true);
            }}
          />
          <ViewServicePartnerModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            partner={selectedPartner}
            loading={updatingServicePartnerLoading}
            onUpdate={handleUpdatePartner}
          />
        </>
      )}
      <AddServiceTicketModal
        isOpen={isServiceTicketModalOpen}
        onClose={() => {
          resetServiceTicketFields();
          setIsServiceTicketModalOpen(false);
        }}
        ticketType={ticketType}
        setTicketType={setTicketType}
        epcName={epcName}
        epcMobileNumber={epcMobileNumber}
        epcPincode={epcPincode}
        siteAddress={siteAddress}
        customerName={customerName}
        customerMobileNumber={customerMobileNumber}
        customerAddress={customerAddress}
        srNo={serialNumber}
        loggerSerialNo={loggerSerialNo}
        complainNote={complainNote}
        inverterModel={inverterModel}
        selectedErrorCode={selectedErrorCode}
        setEpcName={setEpcName}
        setEpcMobileNumber={setEpcMobileNumber}
        setEpcPincode={setEpcPincode}
        setSiteAddress={setSiteAddress}
        setCustomerName={setCustomerName}
        setCustomerMobileNumber={setCustomerMobileNumber}
        setCustomerAddress={setCustomerAddress}
        setSrNo={setSerialNumber}
        setLoggerSerialNo={setLoggerSerialNo}
        setComplainNote={setComplainNote}
        setInverterModel={setInverterModel}
        setSelectedErrorCode={setSelectedErrorCode}
        selectedPhase={selectedPhase}
        setSelectedPhase={setSelectedPhase}
        loading={addServiceTicketLoading}
        handleAddServiceTicket={handleAddServiceTicket}
      />
      {activeTab === "services" && (
        <>
          <ServiceKanbanBoard />
        </>
      )}
    </div>
  );
};

export default ServiceManagement;
